import { ApiError, errorMessage, readJsonBody, type FetchLike, type Suspension } from '../api/client'
import { cachedSupportEmail } from '../api/remoteConfig'
import { APP_VERSION, PLATFORM } from '../lib/version'

/**
 * The four sign-in endpoints of the `api` function (docs/plan/auth.md). None of them takes a bearer
 * token: the email code or the refresh token is the credential.
 */

export interface AuthUser {
  id: string
  email: string
}

/** `/auth/start` answer: the code lives [expiresIn] seconds; another may be asked for after [resendAfter]. */
export interface CodeSent {
  expiresIn: number
  resendAfter: number
}

/** `/auth/verify` and `/auth/refresh` answer. [expiresIn] is the access token's lifetime in seconds. */
export interface Tokens {
  accessToken: string
  expiresIn: number
  refreshToken: string
  user: AuthUser
  /** True only when this sign-in cancelled a pending account deletion (the 30-day grace period). */
  deletionCancelled: boolean
  /** Set when this account is suspended (docs/plan/api.md#moderation): a token is still issued so the owner can
   * export, but signing in never cancels a suspension the way it cancels a user-requested deletion. */
  suspension: Suspension | null
}

/** An HTTP error from an auth endpoint. [retryAfter] is the wait in seconds that 429 answers carry. */
export class AuthApiError extends ApiError {
  readonly retryAfter: number | null

  constructor(status: number, message: string, retryAfter: number | null = null) {
    super(status, message)
    this.name = 'AuthApiError'
    this.retryAfter = retryAfter
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isSeconds(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value !== ''
}

/** The `retryAfter` seconds of an error body, or null when it has none. */
export function retryAfterOf(payload: unknown): number | null {
  return isRecord(payload) && isSeconds(payload.retryAfter) ? Math.ceil(payload.retryAfter) : null
}

function unexpected(status: number): AuthApiError {
  return new AuthApiError(status, 'The server sent an unexpected answer. Try again.')
}

export function parseCodeSent(payload: unknown, status = 200): CodeSent {
  if (isRecord(payload) && isSeconds(payload.expiresIn) && isSeconds(payload.resendAfter)) {
    return { expiresIn: payload.expiresIn, resendAfter: payload.resendAfter }
  }
  throw unexpected(status)
}

export function parseTokens(payload: unknown, status = 200): Tokens {
  if (
    isRecord(payload) &&
    isText(payload.accessToken) &&
    isSeconds(payload.expiresIn) &&
    isText(payload.refreshToken) &&
    isRecord(payload.user) &&
    isText(payload.user.id) &&
    isText(payload.user.email)
  ) {
    return {
      accessToken: payload.accessToken,
      expiresIn: payload.expiresIn,
      refreshToken: payload.refreshToken,
      user: { id: payload.user.id, email: payload.user.email },
      deletionCancelled: payload.deletionCancelled === true,
      // `/auth/verify`'s 200 answer carries `suspended`/`deleteAt` but no `reasonText` of its own (that is a
      // 403/429 error field); a later 403 from any other call fills it in via `suspensionOf`.
      suspension: payload.suspended === true && isText(payload.deleteAt) ? { deleteAt: payload.deleteAt, appealEmail: cachedSupportEmail(), reasonText: null } : null,
    }
  }
  throw unexpected(status)
}

export class AuthApi {
  private readonly apiUrl: string
  private readonly fetchImpl: FetchLike

  constructor(apiUrl: string, fetchImpl?: FetchLike) {
    this.apiUrl = apiUrl
    this.fetchImpl = fetchImpl ?? ((input, init) => fetch(input, init))
  }

  /** Emails a 6-digit code. The answer is the same whether or not the address may sign in. */
  async start(email: string): Promise<CodeSent> {
    const { status, payload } = await this.post('/auth/start', { email })
    return parseCodeSent(payload, status)
  }

  async verify(email: string, code: string): Promise<Tokens> {
    const { status, payload } = await this.post('/auth/verify', { email, code })
    return parseTokens(payload, status)
  }

  /** A new access token for the session. 401 means the session has ended. */
  async refresh(refreshToken: string): Promise<Tokens> {
    const { status, payload } = await this.post('/auth/refresh', { refreshToken })
    return parseTokens(payload, status)
  }

  /** Ends the session on the server; succeeds even when it was already gone. */
  async signOut(refreshToken: string, signal?: AbortSignal): Promise<void> {
    await this.post('/auth/signout', { refreshToken }, signal)
  }

  /** Network failures reject with whatever fetch throws; HTTP errors reject with AuthApiError. */
  private async post(path: string, body: object, signal?: AbortSignal): Promise<{ status: number; payload: unknown }> {
    const response = await this.fetchImpl(`${this.apiUrl}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-macrotide-platform': PLATFORM, 'x-macrotide-version': APP_VERSION },
      body: JSON.stringify(body),
      signal,
    })
    const payload = await readJsonBody(response)
    if (!response.ok) {
      throw new AuthApiError(response.status, errorMessage(payload, response.status), retryAfterOf(payload))
    }
    return { status: response.status, payload }
  }
}
